import { createContext, Dispatch, ReactNode, useReducer } from "react";
import {
  MissingMerchantRateMode,
  PricingStrategy,
  RateTableCarrier,
  SpreadUnit,
  UpsRateType,
  type RateCalculatorAction,
  type RateCalculatorState,
} from "./types";

const initialState: RateCalculatorState = {
  step: 1,
  isOpen: false,

  // Baseline rate tables from global metadata
  baselineRateTables: null,
  baselineRateTablesLoading: false,
  baselineRateTablesError: null,

  // Step 1 - Carrier eligibility (address first for zone lookups)
  merchantAddress: null,
  dailyVolume: 0,
  carrierEligibility: null,
  selectedCarriers: [],
  eligibilityLoading: false,

  // Step 2 - Data input
  dataSource: "historical",
  rateGrid: null,
  serviceLevelStats: null,
  totalShipments: 0,
  oldestShipmentDate: null,
  newestShipmentDate: null,
  dataError: null,
  uploadedFile: null,
  destinationZips: null,

  // Step 3
  pricingStrategy: PricingStrategy.Spread,
  discountAmount: 5,
  spreadAmount: 0.5,
  missingMerchantRateMode: MissingMerchantRateMode.DefaultSpread,
  defaultSpreadNoMerchantRate: 0.5,
  defaultSpreadUnit: SpreadUnit.Dollars,
  dhlSettings: { needsGL: true, weeklyPickups: 5 },
  revShareSettings: {
    usps: { underOneLbPct: 1, overOneLbPct: 4 },
    ups: {
      activeRateType: UpsRateType.GroundSaver,
      groundSaverPct: 5,
      otherServicesPct: 11,
    },
  },

  // Step 4
  quoteResults: null,
};

function rateCalculatorReducer(
  state: RateCalculatorState,
  action: RateCalculatorAction,
): RateCalculatorState {
  switch (action.type) {
    case "OPEN_WIZARD":
      return { ...state, isOpen: true };
    case "CLOSE_WIZARD":
      return { ...state, isOpen: false };
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_BASELINE_RATE_TABLES":
      return {
        ...state,
        baselineRateTables: action.rateTables,
        baselineRateTablesLoading: false,
        baselineRateTablesError: null,
      };
    case "SET_BASELINE_RATE_TABLES_LOADING":
      return { ...state, baselineRateTablesLoading: action.loading };
    case "SET_BASELINE_RATE_TABLES_ERROR":
      return {
        ...state,
        baselineRateTablesError: action.error,
        baselineRateTablesLoading: false,
      };
    case "SET_DATA_SOURCE":
      return { ...state, dataSource: action.source };
    case "SET_RATE_DATA":
      return {
        ...state,
        rateGrid: action.rateGrid,
        serviceLevelStats: action.serviceLevelStats,
        totalShipments: action.totalShipments,
        oldestShipmentDate: action.oldestShipmentDate,
        newestShipmentDate: action.newestShipmentDate,
        dataError: null,
        dailyVolume: 0,
        uploadedFile: action.uploadedFile ?? state.uploadedFile,
        destinationZips: action.destinationZips ?? state.destinationZips,
      };
    case "SET_DATA_ERROR":
      return { ...state, dataError: action.error };
    case "SET_UPLOADED_FILE":
      return { ...state, uploadedFile: action.file };
    case "SET_DESTINATION_ZIPS":
      return { ...state, destinationZips: action.zips };
    case "SET_MERCHANT_ADDRESS":
      return { ...state, merchantAddress: action.address };
    case "SET_DAILY_VOLUME":
      return { ...state, dailyVolume: action.volume };
    case "SET_CARRIER_ELIGIBILITY":
      return {
        ...state,
        carrierEligibility: action.eligibility,
        selectedCarriers: getDefaultSelectedCarriers(action.eligibility),
      };
    case "SET_ELIGIBILITY_LOADING":
      return { ...state, eligibilityLoading: action.loading };
    case "TOGGLE_CARRIER": {
      const carriers = state.selectedCarriers.includes(action.carrier)
        ? state.selectedCarriers.filter((c) => c !== action.carrier)
        : [...state.selectedCarriers, action.carrier];
      return { ...state, selectedCarriers: carriers };
    }
    case "SET_PRICING_STRATEGY":
      return { ...state, pricingStrategy: action.strategy };
    case "SET_DISCOUNT_AMOUNT":
      return { ...state, discountAmount: action.amount };
    case "SET_SPREAD_AMOUNT":
      return { ...state, spreadAmount: action.amount };
    case "SET_MISSING_MERCHANT_RATE_MODE":
      return { ...state, missingMerchantRateMode: action.mode };
    case "SET_DEFAULT_SPREAD_NO_MERCHANT_RATE":
      return { ...state, defaultSpreadNoMerchantRate: action.amount };
    case "SET_DEFAULT_SPREAD_UNIT":
      return { ...state, defaultSpreadUnit: action.unit };
    case "SET_DHL_SETTINGS":
      return { ...state, dhlSettings: action.settings };
    case "SET_REV_SHARE_SETTINGS":
      return { ...state, revShareSettings: action.settings };
    case "SET_QUOTE_RESULTS":
      return { ...state, quoteResults: action.results };
    case "RESET":
      return { ...initialState, isOpen: state.isOpen };
    default:
      return state;
  }
}

function getDefaultSelectedCarriers(
  eligibility: RateCalculatorState["carrierEligibility"],
): RateCalculatorState["selectedCarriers"] {
  if (!eligibility) return [];
  const carriers: RateCalculatorState["selectedCarriers"] = [];
  if (eligibility.dhl?.eligible) carriers.push(RateTableCarrier.DHL_ECS);
  if (eligibility.amazon.eligible) carriers.push(RateTableCarrier.Amazon);
  if (eligibility.ups.eligible) carriers.push(RateTableCarrier.UPS);
  if (eligibility.usps.eligible) carriers.push(RateTableCarrier.USPS);
  if (eligibility.uniuni.eligible) carriers.push(RateTableCarrier.UNIUNI);
  return carriers;
}

export const RateCalculatorStateContext = createContext<
  RateCalculatorState | undefined
>(undefined);

export const RateCalculatorDispatchContext = createContext<
  Dispatch<RateCalculatorAction> | undefined
>(undefined);

export function RateCalculatorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(rateCalculatorReducer, initialState);

  return (
    <RateCalculatorStateContext.Provider value={state}>
      <RateCalculatorDispatchContext.Provider value={dispatch}>
        {children}
      </RateCalculatorDispatchContext.Provider>
    </RateCalculatorStateContext.Provider>
  );
}
